import {
    canReassignLinkedReconciliationAccount,
    isImporterReconciliationQueue,
    isImporterTransactionsQueue,
} from "@common/helpers/financialDocument";
import {
    FinancialDocument,
    FinancialDocumentExtended,
    FinancialDocumentListItem,
    FinancialDocumentListItemIntegrationAccount,
} from "@common/types/financialDocument";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { FinancialDocumentQuery } from "@common/schemas/financialDocument/financial-document-query.schema";
import { FinancialDocumentDetailIncludeQuery } from "@common/schemas/financialDocument/financial-document-detail-include.schema";
import {
    FinancialTransactionForTransactionMatch,
    TransactionMatch,
} from "@common/types/transactionMatch";
import { EagerLoaded } from "@common/types/base/orm";
import { QueryClient } from "@tanstack/react-query";
import { TransactionsFilters } from "../transactions/filters/lib";

export const financialDocumentQueryKeys = {
    all: () => ["financialDocument"] as const,
    lists: () => [...financialDocumentQueryKeys.all(), "lists"] as const,
    list: (queryParams: FinancialDocumentQuery) =>
        [...financialDocumentQueryKeys.lists(), queryParams] as const,
    importerQueues: () =>
        [...financialDocumentQueryKeys.lists(), "importerQueues"] as const,
    importerQueue: (queue: string) =>
        [...financialDocumentQueryKeys.importerQueues(), queue] as const,
    importerQueueWithFilters: (
        queue: string,
        queryParams: FinancialDocumentQuery,
    ) =>
        [
            ...financialDocumentQueryKeys.importerQueue(queue),
            queryParams,
        ] as const,
    details: () => [...financialDocumentQueryKeys.all(), "details"] as const,
    detail: (id: number, includeQuery?: FinancialDocumentDetailIncludeQuery) =>
        [
            ...financialDocumentQueryKeys.details(),
            id,
            includeQuery ?? {},
        ] as const,
} as const;

export function updateFinancialDocumentDetailCaches(
    queryClient: QueryClient,
    documentId: number,
    updater: (
        existing: FinancialDocumentExtended | undefined,
    ) => FinancialDocumentExtended | undefined,
) {
    queryClient.setQueriesData<FinancialDocumentExtended>(
        {
            queryKey: [...financialDocumentQueryKeys.details(), documentId],
            exact: false,
        },
        updater,
    );
}

export function patchFinancialDocumentDetailCaches(
    queryClient: QueryClient,
    documentId: number,
    patch: Partial<FinancialDocument> | Partial<FinancialDocumentExtended>,
) {
    updateFinancialDocumentDetailCaches(queryClient, documentId, (existing) =>
        existing ? { ...existing, ...patch } : undefined,
    );
}

export const financialDocumentFileQueryKeys = {
    all: () => ["financialDocumentFile"] as const,
    file: (id: number) =>
        [...financialDocumentFileQueryKeys.all(), id] as const,
} as const;

export const transactionMatchingQueryKeys = {
    all: () => ["transactionMatching"] as const,
    list: (filtersDto: TransactionFiltersDto) =>
        [...transactionMatchingQueryKeys.all(), filtersDto] as const,
    page: (filtersDto: TransactionFiltersDto, page: number) => [
        ...transactionMatchingQueryKeys.list(filtersDto),
        page,
    ],
} as const;

export const transferMatchingQueryKeys = {
    all: () => ["transferMatching"] as const,
    list: (filtersDto: TransactionsFilters) =>
        [...transferMatchingQueryKeys.all(), filtersDto] as const,
    page: (filtersDto: TransactionsFilters, page: number) => [
        ...transferMatchingQueryKeys.list(filtersDto),
        page,
    ],
} as const;

export const financialDocumentsForFinancialAccountKeys = {
    all: () => ["financialDocumentsForFinancialAccount"] as const,
    list: (accountId: number) =>
        [
            ...financialDocumentsForFinancialAccountKeys.all(),
            accountId,
        ] as const,
} as const;

export interface UploadingDocumentPlaceholder {
    fileName: string;
    isUploading: boolean;
    financialDocument?: FinancialDocument;
    progress?: number;
}

export function getMatchedTransaction(
    financialDocument: FinancialDocumentListItem | FinancialDocumentExtended,
): FinancialTransactionForTransactionMatch | null {
    return (
        financialDocument.transactionMatches[0]?.financialTransaction ?? null
    );
}

export function getTransactionMatchFilters(
    transactionMatches: Array<
        EagerLoaded<TransactionMatch, "financialTransaction">
    >,
): Pick<TransactionsFilters, "transactionIds" | "entityIds"> {
    return {
        transactionIds: transactionMatches.map(
            (match) => match.financialTransaction.id,
        ),
        entityIds: [
            ...new Set(
                transactionMatches.map(
                    (match) => match.financialTransaction.entityId,
                ),
            ),
        ],
    };
}

export function hasTransactionMatches(
    doc: FinancialDocument | FinancialDocumentExtended,
): doc is FinancialDocumentExtended {
    return "transactionMatches" in doc && Array.isArray(doc.transactionMatches);
}

// In the future, we'll replace Document Flow ad-hoc fetches with dedicated DB tables

export type DocumentFlowStatus = "pending" | "done" | null;

export function getDocumentStatusIconColorClass(
    status: NonNullable<DocumentFlowStatus>,
): string {
    return status === "done" ? "icon-color-gray-700" : "icon-color-blue-600";
}

export function getImportStatusLabel(
    status: NonNullable<DocumentFlowStatus>,
): string {
    return status === "done" ? "Imported" : "Import in progress";
}

export function getReconciliationStatusLabel(
    status: NonNullable<DocumentFlowStatus>,
): string {
    return status === "done" ? "Reconciled" : "Queued for reconciliation";
}

export function getMatchStatusLabel(
    status: NonNullable<DocumentFlowStatus>,
): string {
    return status === "done" ? "Matched" : "Awaiting match";
}

export function getImportStatus(
    document: FinancialDocument | FinancialDocumentExtended,
): DocumentFlowStatus {
    const integrationAccount =
        "integrationAccount" in document ? document.integrationAccount : null;
    const isIntegrationLinked =
        (integrationAccount?.financialAccountId ?? null) !== null;

    if (isIntegrationLinked) {
        return "done";
    }

    const isInImporterQueue =
        document.importerQueue !== null &&
        isImporterTransactionsQueue(document.importerQueue);

    if (isInImporterQueue) {
        return "pending";
    }

    return null;
}

export function getReconciliationStatus(
    document: FinancialDocument | FinancialDocumentExtended,
): DocumentFlowStatus {
    if (document.isReconciled) {
        return "done";
    }

    if (
        document.importerQueue !== null &&
        isImporterReconciliationQueue(document.importerQueue)
    ) {
        return "pending";
    }

    return null;
}

type DocumentFieldLockReason = "reconciled" | "imported" | "linked-account";

type DocumentLockIntegrationAccount =
    | IntegrationAccount
    | FinancialDocumentListItemIntegrationAccount
    | null
    | undefined;

type DocumentWithLockFields =
    | FinancialDocument
    | FinancialDocumentListItem
    | FinancialDocumentExtended;

export function getDocumentFieldLockReason(
    document: DocumentWithLockFields,
    integrationAccount: DocumentLockIntegrationAccount,
): DocumentFieldLockReason | null {
    if (document.isReconciled) {
        return "reconciled";
    }

    if (
        integrationAccount?.financialAccountId != null &&
        !canReassignLinkedReconciliationAccount({
            isReconciled: false,
            importerQueue: document.importerQueue,
        })
    ) {
        return "imported";
    }

    if (document.financialAccountId != null) {
        return "linked-account";
    }

    return null;
}

const IMPORT_LOCKED_TOOLTIP = "Cannot edit on imported document";
const RECONCILIATION_LOCKED_TOOLTIP = "Cannot edit on reconciled document";

export function isDocumentFieldLocked(
    document: DocumentWithLockFields,
    integrationAccount: DocumentLockIntegrationAccount,
): boolean {
    return getDocumentLockedTooltip(document, integrationAccount) !== undefined;
}

export function getDocumentLockedTooltip(
    document: DocumentWithLockFields,
    integrationAccount: DocumentLockIntegrationAccount,
): string | undefined {
    const reason = getDocumentFieldLockReason(document, integrationAccount);
    switch (reason) {
        case "reconciled":
            return RECONCILIATION_LOCKED_TOOLTIP;
        case "imported":
            return IMPORT_LOCKED_TOOLTIP;
        case "linked-account":
        case null:
            return undefined;
    }
}

export function getMatchStatus(
    document: FinancialDocumentExtended,
): DocumentFlowStatus {
    if (document.transactionMatches.length > 0) {
        return "done";
    }

    // Skipped adding a "pending" state for now because ~46% of prod docs are "never_scanned" and would show a pending icon.

    return null;
}
